import { useState, type FormEvent } from 'react';
import styles from './LoginPage.module.css';
import authBg from '@/assets/auth_bg.svg';
import { login, LoginError, type Session } from '@/lib/auth';

/**
 * Mirrors the MCP's sign-in screen so the two surfaces are visibly the same
 * product. Credentials go to the dashboard API's `/v1/auth/login`, which
 * proxies the Hector platform — the same identity the MCP authenticates.
 */
export function LoginPage({ onSignedIn }: { onSignedIn: (s: Session) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (busy) return;
    setError(null);

    if (!email.trim() || !password) {
      setError('Enter your email and password.');
      return;
    }

    setBusy(true);
    try {
      onSignedIn(await login(email.trim(), password));
    } catch (err) {
      setError(
        err instanceof LoginError
          ? err.message
          : 'Sign-in service is unreachable. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.page} style={{ ['--auth-bg-image' as string]: `url(${authBg})` }}>
      <form className={styles.card} onSubmit={(e) => { void submit(e); }} noValidate>
        <h1 className={styles.title}>Welcome Back!</h1>
        <p className={styles.subtitle}>Hector Quick Commerce Analytics</p>

        {error && (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}

        <div className={styles.field}>
          <label className={styles.label} htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            className={styles.input}
            placeholder="you@company.com"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="password">
            Password
          </label>
          <div className={styles.inputWrap}>
            <input
              id="password"
              type={showPw ? 'text' : 'password'}
              className={styles.input}
              placeholder="Enter password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className={styles.togglePw}
              tabIndex={-1}
              aria-label="Toggle password visibility"
              onClick={() => setShowPw((v) => !v)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c1.658 0 3.236-.391 4.646-1.088M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className={styles.optionsRow}>
          <span />
          <a
            className={styles.forgot}
            href="https://app.hectorai.live/auth/forgot-password"
            target="_blank"
            rel="noopener noreferrer"
          >
            Forgot Password?
          </a>
        </div>

        <button className={styles.signIn} type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign In'}
        </button>

        <p className={styles.info}>
          Your credentials are sent securely to hectorai.live for verification.
        </p>
      </form>
    </div>
  );
}
